﻿/* custom timepicker provided by https://github.com/IliaIdakiev/angular-material-timepicker */
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { ITimeRange } from '@caslink/common-client/src';
import { MatTimepickerDirective } from 'mat-timepicker';
import { NextInputService } from '../nextInput/NextInputService';
import { StringUtilsService } from '../utilities/StringUtils';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';

const DAY_HOURS = 24;
const DAY_MINUTES = 60;
const MINUTE_SECONDS = 60;
const HALF_OF_DAY_HOURS = DAY_HOURS / 2;
const SECONDS_IN_DAY = DAY_MINUTES * MINUTE_SECONDS * DAY_HOURS;
const MAX_ALLOWED_MINUTES_IN_HOUR = 60 - 1;

enum NextInputType {
    Minutes = 'minutes',
    Hours = 'hours',
}

@Component({
    selector: 'cl-select-time-picker',
    templateUrl: './SelectTimePickerView.html'
})
export class SelectTimePickerComponent implements OnInit, OnDestroy {
    static $name: string = 'clSelectTimePicker2';

    @ViewChild('minutesInput', { static: true }) minutesInput: ElementRef;
    @ViewChild('hoursInput', { static: true }) hoursInput: ElementRef;
    @ViewChild('timePicker', { static: true }) timePicker: MatTimepickerDirective;

    @Input() label: string;
    @Input() showTimepickerDialog: boolean;
    @Input() startEndMode: 'start' | 'end';

    @Input() private parentForm: FormGroup;
    @Input() private intervalAsSeconds: number;
    @Input() private timeRange: ITimeRange;

    defaultTimeValue: Date;
    formatedDate: Date;
    hourFormControlName = 'hoursControl';
    meridiem: 'am' | 'pm';
    meridiemFormControlName = 'meridiemControl';
    minimumTimeFrom: Date | null;
    minutesFormControlName = 'minutesControl';
    nextInputType = NextInputType;
    form: FormGroup;

    ignoreMandatoryGroup = new FormGroup({
        'ignore': new FormControl(),
    });

    private dateFormat = 'h:mmA';
    private nextInputSubscription: Subscription;
    private isChangedFromThisInput: boolean;
    private hours: string;
    private minutes: string;

    constructor(
        private fb: FormBuilder,
        private nextInputService: NextInputService,
        private stringUtils: StringUtilsService,
    ) {
    }

    ngOnInit() {
        this.initalizeForm();
        this.subsribeToNextInput();
    }

    ngOnDestroy() {
        if (this.nextInputSubscription) {
            this.nextInputSubscription.unsubscribe();
        }
    }

    setFocusBehavior() {
        this.isChangedFromThisInput = false;
    }

    preventSwitchToAnotherInputOnKeyDown() {
        this.isChangedFromThisInput = true;
    }

    trySwitchToNextInput(event, nextInputName) {
        const numberRe = /[0-9]/;
        const MAX_LEN = 2;

        if ((nextInputName === this.nextInputType.Hours && this.startEndMode === 'end') ||
            event.target.value.length < MAX_LEN ||
            !this.isChangedFromThisInput ||
            !numberRe.test(event.key) ||
            this.form.status === 'INVALID') {
            return;
        }

        const shouldSwitchToNextRow = nextInputName === this.nextInputType.Hours;
        const startEnd = shouldSwitchToNextRow ? 'end' : this.startEndMode;
        this.nextInputService.setNext([nextInputName, startEnd]);
    }

    updateMinutesFromInput(minutes: number) {
        this.minutes = this.getValueWithLeadingZeros(this.getMinutesFromInterval(minutes));
        this.patchFormValues();
        this.updateTime();
    }

    updateHoursFromInput(hours: number) {
        this.hours = this.getValueWithLeadingZeros(hours);
        this.patchFormValues();
        this.updateTime();
    }

    updateMeridiem(meridiem: "am" | "pm") {
        this.meridiem = meridiem;
        this.patchFormValues();
        this.updateTime();
    }

    timePickerChangeHandler(event: Date) {
        const momentTime = moment(event);
        momentTime.set('minutes', this.getMinutesFromInterval(momentTime.get('minutes')));
        this.extract12hFormat(momentTime);
        this.setTimeRange(momentTime.format('h:mm a'));
        this.patchFormValues();
    }

    private getMinutesFromInterval(minutes: number) {
        if (!this.intervalAsSeconds) {
            return minutes;
        }

        const intervalMinutes = this.intervalAsSeconds / 60;
        if (intervalMinutes > 60) {
            throw new Error(`Interval can't be larger than 1 hour`);
        }

        const closestMultipleOfinterval = Math.round(minutes / intervalMinutes) * intervalMinutes;
        return closestMultipleOfinterval > MAX_ALLOWED_MINUTES_IN_HOUR ? 0 : closestMultipleOfinterval;
    }

    private initalizeForm() {
        this.extractDateFromTimeString();
        this.initializeForm();
        this.addFromToParent();
        this.subscribeToStartValue();
        this.setMinimumEndTimeFromStart(this.parentForm.controls.start.value);
    }

    private addFromToParent() {
        this.parentForm.addControl(this.startEndMode, this.form);
    }

    private extractDateFromTimeString() {
        const momentTime = moment(this.timeRange[this.startEndMode + 'Time'], this.dateFormat);
        this.extract12hFormat(momentTime);
    }

    private subscribeToStartValue() {
        this.parentForm.valueChanges
            .subscribe((change) => {
                this.setMinimumEndTimeFromStart(change.start);
            });
    }

    private setMinimumEndTimeFromStart(change: { hoursControl: string; minutesControl: string; meridiemControl: string }) {
        const timeString = `${change.hoursControl}:${change.minutesControl} ${change.meridiemControl}`;

        const intervalMinutes = this.intervalAsSeconds ? this.intervalAsSeconds / 60 : 0;
        const isMinimumTimeAlreadySet = (moment(this.minimumTimeFrom).diff(moment(timeString, this.dateFormat).add(intervalMinutes, 'minutes')) === 0);

        if (this.startEndMode === 'end' && !isMinimumTimeAlreadySet) {
            this.minimumTimeFrom = moment(timeString, this.dateFormat).add(intervalMinutes, 'minutes').toDate();
            this.alwaysAllow12AMAtDirective();
        }
    }

    private alwaysAllow12AMAtDirective() {
        setTimeout(() => {
            const allowedMapExists = this.timePicker['allowed12HourMap'];
            if (allowedMapExists) {
                this.timePicker['allowed12HourMap'].am[12][0] = true;
            }
        }, 0);
    }

    private extract12hFormat(momentTime: moment.Moment) {
        const hours = momentTime.hour();
        const minute = momentTime.minute();

        this.meridiem = momentTime.hour() >= HALF_OF_DAY_HOURS ? 'pm' : 'am';
        this.hours = this.getValueWithLeadingZeros(hours % HALF_OF_DAY_HOURS || HALF_OF_DAY_HOURS);
        this.minutes = this.getValueWithLeadingZeros(this.getMinutesFromInterval(minute));
    }

    private getValueWithLeadingZeros(input: number, defaultLength = 2) {
        return input.toString().padStart(defaultLength, '0');
    }

    private initializeForm() {
        this.form = this.fb.group({
            [this.startEndMode]: ['', [Validators.required]],
            [this.hourFormControlName]: ['', [Validators.required, Validators.max(HALF_OF_DAY_HOURS), Validators.min(1)]],
            [this.minutesFormControlName]: ['', [Validators.required, Validators.max(MAX_ALLOWED_MINUTES_IN_HOUR), Validators.min(0)]],
            [this.meridiemFormControlName]: ['', [Validators.required]],
        });
        this.patchFormValues();
    }

    private patchFormValues() {
        this.form.patchValue({
            [this.startEndMode]: moment(this.timeRange[this.startEndMode + 'Time'], this.dateFormat).toDate(),
            [this.hourFormControlName]: this.hours,
            [this.minutesFormControlName]: this.minutes,
            [this.meridiemFormControlName]: this.meridiem,
        });
    }

    private updateTime() {
        const timeString = this.getTimeString();
        this.form.patchValue({
            [this.startEndMode]: moment(timeString, this.dateFormat).toDate(),
        });
        this.setTimeRange(this.getTimeString());
    }

    private getTimeString() {
        const hour = this.form.get(this.hourFormControlName).value;
        const minute = this.form.get(this.minutesFormControlName).value;
        const meridiem = this.form.get(this.meridiemFormControlName).value;

        return `${hour} ${minute} ${meridiem}`;
    }

    private setTimeRange(timeString: string) {
        this.timeRange[this.startEndMode + 'Time'] = timeString;
        this.timeRange[this.startEndMode] = this.getTimeInSeconds(timeString);
        this.parentForm.updateValueAndValidity();
    }

    private getTimeInSeconds(timeString: string) {
        let seconds = this.stringUtils.timeStringToSeconds(timeString);

        if (seconds === 0 && this.startEndMode === 'end') {
            seconds = SECONDS_IN_DAY;
        }
        return seconds;
    }

    private subsribeToNextInput() {
        this.nextInputService.nextInput$.subscribe(
            ([inputType, startEndMode]) => {
                if (startEndMode === this.startEndMode) {
                    if (inputType === this.nextInputType.Minutes) {
                        this.minutesInput.nativeElement.focus();
                    } else {
                        this.hoursInput.nativeElement.focus();
                    }
                }
            }
        );
    }
}
